import { addAction } from "@wayflyer/blackbox-fe";
import { FlyError, Link, Text } from "@wayflyer/flyui";
import { useAxios } from "auth-sdk";
import { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useErrorModal } from "shared-ui";
import { handleContactTeam } from "shared-utils";

import { MESSAGES } from "./message-definitions";

const EMAIL_VERIFICATION_SEND_PATH = "/company-creation/api/email-verification/send";
const RESEND_COOLDOWN_MS = 30_000;

export interface UseEmailVerificationResend {
  sendVerificationEmail: () => void;
  isSending: boolean;
  recentlySent: boolean;
}

export const useEmailVerificationResend = (): UseEmailVerificationResend => {
  const intl = useIntl();
  const { post } = useAxios();
  const { openErrorModal } = useErrorModal();
  const [isSending, setIsSending] = useState(false);
  const [recentlySent, setRecentlySent] = useState(false);

  const sendVerificationEmail = useCallback(() => {
    if (isSending || recentlySent) {
      // A request is already in flight, or we're still inside the post-send cooldown.
      return;
    }
    setIsSending(true);
    post(EMAIL_VERIFICATION_SEND_PATH)
      .then(() => {
        addAction("email verification request sent");
        setRecentlySent(true);
        setTimeout(() => {
          setRecentlySent(false);
        }, RESEND_COOLDOWN_MS);
      })
      .catch((error: Error) => {
        openErrorModal(
          new FlyError({
            title: intl.formatMessage(MESSAGES.errorTitle),
            message: (
              <FormattedMessage
                id="funding-application: verification email request failed message"
                defaultMessage="<p>We were unable to send the verification email. If the problem persists please <a>contact our support team</a>.</p>"
                values={{
                  p: (chunks: string) => <Text>{chunks}</Text>,
                  a: (chunks: string) => <Link onClick={handleContactTeam}>{chunks}</Link>,
                }}
              />
            ),
            error,
          })
        );
      })
      .finally(() => {
        setIsSending(false);
      });
  }, [isSending, recentlySent, post, openErrorModal, intl]);

  return { sendVerificationEmail, isSending, recentlySent };
};
